import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, DoorOpen, Loader2, MailCheck, ShieldCheck } from 'lucide-react';
import { useState } from 'react';
import { EmptyState } from '../components/EmptyState';
import { HandoverLink } from '../components/HandoverLink';
import { HelpHint } from '../components/HelpHint';
import { Tooltip } from '../components/Hover';
import { PageNav } from '../components/PageNav';
import { useToast } from '../components/Toast';
import { api } from '../lib/api';

/**
 * People asking to be let in.
 *
 * Signing in with Google does not create an account here — it creates a row on
 * this screen. Approving is what creates the account, and the person then has to
 * confirm from an emailed link before it works. Three gates, none of which the
 * person arriving controls, and this page is the middle one.
 *
 * The design decision that matters: **the role selector has no default.** An
 * approve button that picks a role for you is a mis-click away from handing
 * somebody every customer record in Kisii, and the API refuses an approval
 * without one for exactly that reason. So the button stays disabled until an
 * admin chooses.
 */
export function AccessRequestsPage() {
  const queryClient = useQueryClient();
  const toast = useToast();
  /** The role chosen per request. Nothing is pre-selected, deliberately. */
  const [roles, setRoles] = useState<Record<string, string>>({});
  const [rejecting, setRejecting] = useState<string | null>(null);
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);
  /**
   * Activation links, kept by request id.
   *
   * Not a single value: an admin working through four requests would otherwise
   * see the fourth link replace the third before they had sent it.
   */
  const [links, setLinks] = useState<Record<string, { url: string; who: string }>>({});

  const list = useQuery({ queryKey: ['access-requests'], queryFn: () => api.accessRequests() });

  const refresh = async () => {
    setError(null);
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['access-requests'] }),
      // Approving creates a user, so the users list and the tray are both stale.
      queryClient.invalidateQueries({ queryKey: ['users'] }),
      queryClient.invalidateQueries({ queryKey: ['notifications'] }),
    ]);
  };

  const approve = useMutation({
    mutationFn: async (id: string) => ({
      id,
      who: requests.find((request) => request.id === id)?.displayName ?? 'them',
      result: await api.approveRequest(id, roles[id] ?? ''),
    }),
    onSuccess: async ({ id, who, result }) => {
      setLinks((current) => ({ ...current, [id]: { url: result.activationUrl, who } }));
      await refresh();
    },
    onError: (e: Error) => setError(e.message),
  });

  const reject = useMutation({
    mutationFn: (id: string) => api.rejectRequest(id, reason || undefined),
    onSuccess: async () => {
      setRejecting(null);
      setReason('');
      await refresh();
    },
    onError: (e: Error) => setError(e.message),
  });

  /*
   * Undo a rejection.
   *
   * Without this a turned-down request is permanent: signing in again finds
   * the rejected row and refuses for ever, showing whatever reason was typed
   * at the time. A mis-click was a life sentence, and the message the system
   * gave an admin — ask them to sign in again — did not work.
   */
  const reopen = useMutation({
    mutationFn: (id: string) => api.reopenRequest(id),
    onSuccess: async () => {
      toast.done('They can be considered again. Ask them to sign in with Google once more.');
      await queryClient.invalidateQueries({ queryKey: ['access-requests'] });
    },
    onError: (e: Error) => toast.failed(e.message),
  });

  const resend = useMutation({
    mutationFn: async (id: string) => ({
      id,
      who: requests.find((request) => request.id === id)?.displayName ?? 'them',
      result: await api.resendActivation(id),
    }),
    onSuccess: async ({ id, who, result }) => {
      setLinks((current) => ({ ...current, [id]: { url: result.activationUrl, who } }));
      await refresh();
    },
    onError: (e: Error) => setError(e.message),
  });

  const requests = list.data?.requests ?? [];
  const assignable = list.data?.assignable ?? [];
  const pending = requests.filter((request) => request.status === 'pending');
  const decided = requests.filter((request) => request.status !== 'pending');

  return (
    <div className="space-y-6">
      <PageNav crumbs={[{ label: 'Users', to: '/users' }, { label: 'Access requests' }]} />

      <div>
        <h1 className="flex items-center gap-1.5 font-heading text-2xl">
          Access requests
          <HelpHint>
            Somebody signing in with Google or GitHub does not get an account — they get a row here.
            Approving creates the account with the role you choose, and emails them a link they must
            open before it works. Nothing you do here lets anybody in on its own.
          </HelpHint>
        </h1>
        <p className="mt-1 max-w-2xl text-sm text-muted-foreground">
          {pending.length === 0
            ? 'Nobody is waiting.'
            : `${pending.length} ${pending.length === 1 ? 'person is' : 'people are'} waiting for you.`}
        </p>
      </div>

      {error && (
        <p className="rounded-xl border border-danger-200 bg-danger-50 px-4 py-3 text-sm text-danger-700">
          {error}
        </p>
      )}

      {/*
        Links to hand over, at the top of the page.

        Not inside the request's own card, because approving moves that card out
        of the waiting list and into "already decided" — the link would appear
        for a moment and then jump somewhere else while the admin was reading
        it. Up here it stays put and stays visible while they work down the rest
        of the queue.
      */}
      {Object.entries(links).map(([id, link]) => (
        <HandoverLink
          key={id}
          url={link.url}
          who={link.who}
          what="Open this to finish setting up your account."
        />
      ))}

      {list.isPending && (
        <p className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
          Loading
        </p>
      )}

      {list.data && requests.length === 0 && (
        <EmptyState
          icon={DoorOpen}
          headline="Nobody has asked for access"
          body="When somebody signs in with Google or GitHub for the first time, they appear here for you to approve with a role. Sign-in providers are switched on in Settings."
          secondary={{ label: 'Open settings', to: '/settings' }}
        />
      )}

      {pending.map((request) => (
        <div key={request.id} className="card space-y-4">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="min-w-0">
              <p className="font-medium">{request.displayName ?? request.email}</p>
              <p className="text-sm text-muted-foreground">{request.email}</p>
              <div className="mt-2 flex flex-wrap items-center gap-2 text-xs">
                <span className="badge bg-muted text-muted-foreground">via {request.provider}</span>

                {/*
                  Whether the provider vouched for the address, said in words.
                  It is the single most load-bearing fact on this card: an
                  unverified address is how an account takeover would be
                  attempted, and it decides what the system was willing to do
                  automatically. Colour is not the signal — the words are.
                */}
                {request.emailVerified ? (
                  <span className="badge bg-navy-50 text-navy-700">
                    <ShieldCheck className="h-3 w-3" aria-hidden="true" />
                    address confirmed by {request.provider}
                  </span>
                ) : (
                  <span className="badge bg-accent-50 text-accent-700">
                    <AlertTriangle className="h-3 w-3" aria-hidden="true" />
                    address not confirmed — check who this is
                  </span>
                )}

                <span className="text-muted-foreground">
                  asked {new Date(request.createdAt).toLocaleString('en-KE')}
                </span>
              </div>
              {request.note && (
                <p className="mt-2 text-sm text-muted-foreground">
                  They said: “{request.note}”
                </p>
              )}
            </div>
          </div>

          <div className="flex flex-wrap items-end gap-3">
            <label className="text-sm">
              What will they do?
              <select
                className="input mt-1"
                value={roles[request.id] ?? ''}
                onChange={(event) => setRoles({ ...roles, [request.id]: event.target.value })}
              >
                {/* No default. The API refuses an approval without a role, and
                    this is the same rule where somebody can see it. */}
                <option value="">Choose a role</option>
                {assignable.map((role) => (
                  <option key={role} value={role}>
                    {role}
                  </option>
                ))}
              </select>
            </label>

            <Tooltip
              label={
                roles[request.id]
                  ? `Creates the account as ${roles[request.id]} and emails them a link to confirm it`
                  : 'Choose a role first — an approval with no role chosen is a mis-click'
              }
              side="top"
            >
              <button
                type="button"
                className="btn-primary"
                disabled={!roles[request.id] || approve.isPending}
                onClick={() => approve.mutate(request.id)}
              >
                {approve.isPending && approve.variables === request.id && (
                  <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                )}
                Approve
              </button>
            </Tooltip>

            <button
              type="button"
              className="btn-quiet"
              onClick={() => {
                setRejecting(rejecting === request.id ? null : request.id);
                setReason('');
              }}
            >
              Turn down
            </button>
          </div>

          {rejecting === request.id && (
            <div className="space-y-2 border-t border-border pt-3">
              <label className="block text-sm">
                Why, if you want to tell them (optional)
                <input
                  className="input mt-1"
                  value={reason}
                  onChange={(event) => setReason(event.target.value)}
                  placeholder="We did not recognise this name"
                  maxLength={300}
                />
              </label>
              {/* Said plainly, because an admin typing here is about to send a
                  stranger a sentence with the company's name on it. */}
              <p className="text-xs text-muted-foreground">
                This is sent to them word for word. Left empty, they are told only that the request
                was not approved — nothing is invented on your behalf.
              </p>
              <button
                type="button"
                className="btn-secondary text-sm"
                disabled={reject.isPending}
                onClick={() => reject.mutate(request.id)}
              >
                {reject.isPending && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />}
                Turn this request down
              </button>
            </div>
          )}
        </div>
      ))}

      {decided.length > 0 && (
        <div className="card">
          <h2 className="font-heading text-lg">Already decided</h2>
          <p className="mt-0.5 text-sm text-muted-foreground">
            Kept so “who let this person in, and when” is answerable later.
          </p>

          <ul className="mt-3 divide-y divide-border">
            {decided.map((request) => (
              <li key={request.id} className="flex flex-wrap items-center justify-between gap-3 py-3">
                <div className="min-w-0 text-sm">
                  <span className="font-medium">{request.displayName ?? request.email}</span>
                  <span className="block text-xs text-muted-foreground">
                    {request.email} · via {request.provider}
                  </span>
                  <span className="block text-xs text-muted-foreground">
                    {request.status === 'approved'
                      ? `approved as ${request.assignedRole} by ${request.decidedBy?.name ?? 'an admin'}`
                      : `turned down by ${request.decidedBy?.name ?? 'an admin'}${
                          request.rejectedReason ? ` — ${request.rejectedReason}` : ''
                        }`}
                    {request.decidedAt
                      ? ` on ${new Date(request.decidedAt).toLocaleDateString('en-KE')}`
                      : ''}
                  </span>
                </div>

                {/*
                  Only for an approval that never landed. Two days is generous
                  and people still miss it, and without this the only remedy is
                  to delete the account and re-approve — which loses the record
                  of the original decision.
                */}
                {request.status === 'rejected' && (
                  <Tooltip label="Puts it back in the queue so they can be approved" side="left">
                    <button
                      type="button"
                      className="btn-quiet shrink-0 text-xs"
                      disabled={reopen.isPending}
                      onClick={() => reopen.mutate(request.id)}
                    >
                      Let them ask again
                    </button>
                  </Tooltip>
                )}

                {request.status === 'approved' && (
                  <Tooltip label="Issues a fresh link and emails it again" side="left">
                    <button
                      type="button"
                      className="btn-quiet shrink-0 text-xs"
                      disabled={resend.isPending}
                      onClick={() => resend.mutate(request.id)}
                    >
                      <MailCheck className="h-3.5 w-3.5" aria-hidden="true" />
                      Send the link again
                    </button>
                  </Tooltip>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
